import * as React from 'react';

import { Button } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { LucidePlusCircle } from 'lucide-react';

import {
  $brokeredOrderForm,
  BrokeredOrderFormState,
} from '../../_state/brokeredOrderForm.state';
import { $brokeredOrderMarginsDataByParams } from '../../_state/brokeredOrderMargins.state';
import { useBrokeredOrderLineItemsManager } from '../../hooks/useBrokeredOrderLineItemsManager';
import { useTranslations } from '../../i18n/useTranslations';
import { BrokeredOrderLineItemRecord } from '../../types/brokeredOrderLineItem';
import { BrokeredOrderCreateLineItemsTable } from '../_shared/LineItems';

type BrokeredOrderWizardStepMaterialsProps = {
  disabled?: boolean;
};

const formSelector = (state: BrokeredOrderFormState) => ({
  lineItems: state.lineItems,
  purchaseOrderIds: state.purchaseOrderIds,
  salesOrderIds: state.salesOrderIds,
});

const $formMaterials = selectAtom($brokeredOrderForm, formSelector, isEqual);

export const BrokeredOrderWizardStepMaterials: React.FC<
  BrokeredOrderWizardStepMaterialsProps
> = ({ disabled = false }) => {
  const t = useTranslations();
  const form = useAtomValue($formMaterials);
  const updateForm = useSetAtom($brokeredOrderForm);
  const marginsData = useAtomValue(
    $brokeredOrderMarginsDataByParams({
      purchaseOrderIds: form.purchaseOrderIds,
      salesOrderIds: form.salesOrderIds,
    }),
  );

  const handleLineItemsChange = React.useCallback(
    (items: BrokeredOrderLineItemRecord[]) => {
      updateForm((prev) => ({ ...prev, lineItems: items }));
    },
    [updateForm],
  );

  const {
    addLineItem,
    addLineItemsFromOrders,
    updateLineItem,
    deleteLineItem,
    hasUnaddedOrderItems,
  } = useBrokeredOrderLineItemsManager({
    lineItems: form.lineItems,
    onLineItemsChange: handleLineItemsChange,
    marginsData,
  });

  if (form.lineItems.length === 0) {
    return (
      <div className="@BrokeredOrderWizardStepMaterials">
        <div className="py-12 text-center">
          <div className="mb-6 text-neutral/50">
            <p className="mb-2 text-lg">
              {t.orders.materials.emptyState.title}
            </p>
            <p className="text-sm">
              {t.orders.materials.emptyState.description}
            </p>
          </div>
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              color="neutral"
              onPress={addLineItem}
              size="large"
              icon={<LucidePlusCircle />}
              isDisabled={disabled}
            >
              {t.orders.materials.buttons.addMaterial}
            </Button>
            {hasUnaddedOrderItems && (
              <Button
                variant="outline"
                color="neutral"
                onPress={addLineItemsFromOrders}
                size="large"
                icon={<LucidePlusCircle />}
                isDisabled={disabled}
              >
                {t.orders.materials.buttons.addFromOrders}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="@BrokeredOrderWizardStepMaterials p-4">
      <BrokeredOrderCreateLineItemsTable
        lineItems={form.lineItems}
        marginsData={marginsData}
        onUpdateLineItem={updateLineItem}
        onDeleteLineItem={deleteLineItem}
        onAddLineItem={addLineItem}
        disabled={disabled}
      />
      {hasUnaddedOrderItems && (
        <Button
          variant="outline"
          color="neutral"
          onPress={addLineItemsFromOrders}
          size="large"
          icon={<LucidePlusCircle />}
          isDisabled={disabled}
          className="mt-4"
        >
          {t.orders.materials.buttons.addFromOrders}
        </Button>
      )}
    </div>
  );
};
